import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';

/** Extra rows above/below the viewport so fast scrolling does not flash empty. */
const DEFAULT_OVERSCAN = 8;

/**
 * Fixed-row-height window for the keyword-cloud value list (TD-2626 D).
 * Mounts only the visible slice plus overscan — ~1.5k values stay scrollable
 * without mounting every checkbox into the DOM.
 *
 * Uses a real `<ul>`/`<li>` (Sonar a11y). Scroll-range spacers are real `<li>`s
 * (not padding): with global `box-sizing: border-box`, padding on the same box
 * as `height`/`maxHeight` inflates the rendered height past the viewport.
 */
function KeywordCloudVirtualList({
  items,
  rowHeight,
  maxHeight,
  renderRow,
  className,
  overscan = DEFAULT_OVERSCAN
}) {
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const totalHeight = items.length * rowHeight;
  const viewportHeight = Math.min(maxHeight, totalHeight);
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const end = Math.min(items.length, start + visibleCount);
  const offsetY = start * rowHeight;
  const bottomPad = Math.max(0, totalHeight - offsetY - (end - start) * rowHeight);

  return (
    <ul
      className={className}
      style={{
        height: viewportHeight,
        maxHeight,
        overflowY: 'auto',
        listStyle: 'none',
        margin: 0,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 4
      }}
      onScroll={onScroll}
    >
      {offsetY > 0 && (
        <li className="keyword-cloud-virt-pad" style={{ height: offsetY }} aria-hidden="true" />
      )}
      {items.slice(start, end).map((item, i) => (
        <li key={item[0]} className="keyword-cloud-virt-row" style={{ height: rowHeight }}>
          {renderRow(item, start + i)}
        </li>
      ))}
      {bottomPad > 0 && (
        <li className="keyword-cloud-virt-pad" style={{ height: bottomPad }} aria-hidden="true" />
      )}
    </ul>
  );
}

KeywordCloudVirtualList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.array).isRequired,
  rowHeight: PropTypes.number.isRequired,
  maxHeight: PropTypes.number.isRequired,
  renderRow: PropTypes.func.isRequired,
  className: PropTypes.string,
  overscan: PropTypes.number
};

export default KeywordCloudVirtualList;
